import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Rollmatris from "../components/Rollmatris";
import { toast } from "sonner";
import { Loader2, Plus } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import TopBar from "../components/TopBar";
import NodePalette from "../components/NodePalette";
import OperationsMap from "../components/OperationsMap";
import FlowMap from "../components/FlowMap";
import LinksModal from "../components/LinksModal";
import SettingsModal from "../components/SettingsModal";
import SystemModal from "../components/SystemModal";
import DetailPanel from "../components/DetailPanel";
import AiPanel from "../components/AiPanel";
import BuoyPanel from "../components/BuoyPanel";
import { AuditModal, FlowDialog, Modal, ProjectDialog, SourcesModal, VersionsModal } from "../components/Modals";

const ALL_LAYERS = { relationellt: true, strukturellt: true, systemiskt: true, digitalt: true, samhalleligt: true };

export default function Workspace() {
    const [projects, setProjects] = useState([]);
    const [projectId, setProjectId] = useState(null);
    const [graph, setGraph] = useState(null);
    const [view, setView] = useState("verksamhet");
    const [layers, setLayers] = useState(ALL_LAYERS);
    const [selection, setSelection] = useState(null);
    const [tab, setTab] = useState("detaljer");
    const [modal, setModal] = useState(null);
    const [flowDialog, setFlowDialog] = useState(false);
    const [pendingTrash, setPendingTrash] = useState(null);
    const [searchParams] = useSearchParams();

    const loadGraph = useCallback(async (pid) => {
        const r = await api.get(`/projects/${pid}/graph`);
        setGraph(r.data);
    }, []);

    useEffect(() => {
        api.get("/projects")
            .then((r) => {
                setProjects(r.data);
                const wanted = searchParams.get("projekt");
                const match = wanted && r.data.find((p) => p.id === wanted);
                if (r.data.length) setProjectId(match ? match.id : r.data[0].id);
            })
            .catch((e) => toast.error(formatApiError(e)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!projectId) return;
        setGraph(null);
        setSelection(null);
        loadGraph(projectId).catch((e) => toast.error(formatApiError(e)));
    }, [projectId, loadGraph]);

    const refresh = useCallback(() => projectId && loadGraph(projectId), [projectId, loadGraph]);

    const NEUTRUM = { syfte: true, system: true, dokument: true };
    const addNode = async (type, x, y) => {
        const count = graph?.nodes.length || 0;
        const label = `${NEUTRUM[type] ? "Nytt" : "Ny"} ${type}`;
        const px = x ?? 140 + (count % 6) * 220;
        const py = y ?? 100 + (Math.floor(count / 6) % 4) * 130 + (count % 3) * 35;
        try {
            const r = await api.post(`/projects/${projectId}/nodes`, { type, label, x: px, y: py });
            await refresh();
            setSelection({ kind: "node", id: r.data.id });
            setTab("detaljer");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const moveNode = async (id, pos) => {
        api.put(`/nodes/${id}/position`, pos).catch(() => {});
    };

    const connectNodes = async (source, target) => {
        try {
            const r = await api.post(`/projects/${projectId}/relations`, { source, target, label: "", perspective: "relationellt" });
            await refresh();
            setSelection({ kind: "relation", id: r.data.id });
            setTab("detaljer");
            toast.success("Relation skapad – ge den etikett och perspektiv i panelen");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const confirmTrash = async () => {
        try {
            await api.delete(`/nodes/${pendingTrash}`);
            if (selection?.kind === "node" && selection.id === pendingTrash) setSelection(null);
            setPendingTrash(null);
            toast.success("Nod borttagen");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e));
            setPendingTrash(null);
        }
    };

    const onSelect = (sel) => {
        setSelection(sel);
        if (sel?.kind === "buoy") setTab("bojar");
        else if (sel) setTab("detaljer");
    };

    const onProjectCreated = (p) => {
        setProjects((prev) => [...prev, p]);
        setProjectId(p.id);
    };

    return (
        <div className="h-screen flex flex-col bg-abyss" data-testid="workspace">
            <TopBar
                projects={projects}
                projectId={projectId}
                onProjectChange={setProjectId}
                onNewProject={() => setModal("project")}
                view={view}
                onViewChange={setView}
                layers={layers}
                onToggleLayer={(id) => setLayers((l) => ({ ...l, [id]: !l[id] }))}
                onOpenModal={setModal}
            />

            <div className="flex flex-1 min-h-0">
                {view === "verksamhet" && graph && <NodePalette onAdd={(type) => addNode(type)} />}

                <main className="flex-1 relative min-w-0">
                    {!graph ? (
                        <div className="absolute inset-0 canvas-bg flex items-center justify-center">
                            <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-slate-500">
                                <Loader2 className="w-4 h-4 animate-spin" /> Laddar kartan …
                            </div>
                        </div>
                    ) : view === "roller" ? (
                        <Rollmatris projectId={projectId}
                            onOpenNode={(id) => { setSelection({ kind: "node", id }); setTab("detaljer"); setView("verksamhet"); }} />
                    ) : view === "verksamhet" ? (
                        <OperationsMap
                            graph={graph}
                            layers={layers}
                            selection={selection}
                            onSelect={onSelect}
                            onMoveNode={moveNode}
                            onConnectNodes={connectNodes}
                            onDropType={(type, x, y) => addNode(type, x, y)}
                            onTrashNode={setPendingTrash}
                        />
                    ) : (
                        <>
                            <FlowMap graph={graph} layers={layers} selection={selection} onSelect={onSelect} />
                            <button
                                data-testid="add-flow-button"
                                onClick={() => setFlowDialog(true)}
                                className="absolute top-4 left-4 flex items-center gap-2 glass-panel rounded-lg px-3 py-2 text-xs font-semibold text-current hover:bg-current/10 transition-colors"
                            >
                                <Plus className="w-4 h-4" /> Lägg till flöde
                            </button>
                        </>
                    )}
                </main>

                {view !== "roller" && (
                <aside className="w-[300px] xl:w-[380px] shrink-0 border-l border-border glass-panel flex flex-col min-h-0" data-testid="right-panel">
                    <div className="flex border-b border-border">
                        {[
                            { id: "detaljer", label: "Detaljer" },
                            { id: "bojar", label: "Bojar" },
                            { id: "ai", label: "AI-assistent" },
                        ].map((t) => (
                            <button key={t.id} data-testid={`panel-tab-${t.id}`} onClick={() => setTab(t.id)}
                                className={`flex-1 py-2.5 text-xs font-semibold transition-colors ${tab === t.id ? "text-current border-b-2 border-current bg-current/5" : "text-slate-500 hover:text-slate-300"}`}>
                                {t.label}
                            </button>
                        ))}
                    </div>
                    {graph && tab === "detaljer" && (
                        <DetailPanel selection={selection} graph={graph} projectId={projectId} refresh={refresh} onClose={() => setSelection(null)} />
                    )}
                    {graph && tab === "bojar" && (
                        <BuoyPanel projectId={projectId} buoys={graph.buoys} nodes={graph.nodes} refresh={refresh} />
                    )}
                    {graph && tab === "ai" && (
                        <AiPanel projectId={projectId} drafts={graph.drafts} refresh={refresh} />
                    )}
                </aside>
                )}
            </div>

            <ProjectDialog open={modal === "project"} onClose={() => setModal(null)} onCreated={onProjectCreated} />
            {graph && <LinksModal open={modal === "links"} onClose={() => setModal(null)} projectId={projectId} buoys={graph.buoys} refresh={refresh} />}
            <SettingsModal open={modal === "settings"} onClose={() => setModal(null)} />
            {graph && <SystemModal open={modal === "system"} onClose={() => setModal(null)} graph={graph} />}
            {graph && <SourcesModal open={modal === "sources"} onClose={() => setModal(null)} projectId={projectId} sources={graph.sources} refresh={refresh} />}
            <AuditModal open={modal === "audit"} onClose={() => setModal(null)} projectId={projectId} />
            <VersionsModal open={modal === "versions"} onClose={() => setModal(null)} projectId={projectId} onRestored={refresh} />
            {graph && <FlowDialog open={flowDialog} onClose={() => setFlowDialog(false)} projectId={projectId} nodes={graph.nodes} onSaved={refresh} />}

            <Modal open={!!pendingTrash} onClose={() => setPendingTrash(null)} title="Ta bort nod?" testid="trash-confirm-modal">
                <p className="text-sm text-slate-400 leading-relaxed mb-5">
                    Noden <span className="text-slate-100 font-semibold">{graph?.nodes.find((n) => n.id === pendingTrash)?.label}</span> tas bort
                    tillsammans med dess relationer, flöden och dammar. Länkade källor och bojar ligger kvar men kopplas loss.
                </p>
                <div className="flex gap-2">
                    <button data-testid="trash-confirm-button" onClick={confirmTrash}
                        className="flex-1 bg-red-500/15 border border-red-500/50 text-red-400 font-semibold text-sm rounded-lg py-2.5 hover:bg-red-500/25 transition-colors">
                        Ta bort noden
                    </button>
                    <button data-testid="trash-cancel-button" onClick={() => setPendingTrash(null)}
                        className="flex-1 border border-border text-slate-300 text-sm rounded-lg py-2.5 hover:border-slate-500 transition-colors">
                        Avbryt
                    </button>
                </div>
            </Modal>
        </div>
    );
}
